import { useQuery, useMutation } from '@tanstack/react-query';
import { getClonedVoiceModels, deleteClonedVoiceModel } from '@/api/voice';
import { notification } from 'antd';
import { useMemo } from 'react';
import { getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';

export const useClonedVoiceModels = (selectedCard: string | null) => {
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const { data, isPending, refetch } = useQuery({
		queryKey: ['clonedVoiceModels', selectedAgentId],
		queryFn: () => getClonedVoiceModels({ agentId: selectedAgentId as string }),
		staleTime: 1000 * 60 * 60,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const sortedClonedVoiceModels = useMemo(() => {
		return [...(data?.data?.data || [])].sort((a, b) => {
			if (a.voiceId === selectedCard) {
				return -1;
			}
			if (b.voiceId === selectedCard) {
				return 1;
			}
			return 0;
		});
	}, [data, selectedCard]);

	const { mutate: deleteClonedVoiceMutation, isPending: isPendingDelete } = useMutation({
		mutationKey: ['deleteClonedVoice'],
		mutationFn: deleteClonedVoiceModel,
		onSuccess: ({ success, message }) => {
			if (success) {
				notification.success({ message: message || 'Voice deleted successfully!' });
				refetch();
			}
		},
		onError: () => {
			notification.error({ message: 'Some error occurred during voice model deletion' });
		},
	});

	return {
		clonedVoiceModels: {
			data: sortedClonedVoiceModels,
			total: data?.data?.total || 0,
			lastEvaluatedKey: data?.data?.lastEvaluatedKey || 0,
		},
		isPending,
		isPendingDelete,
		refetch,
		deleteClonedVoiceMutation,
	};
};
